import React, { useEffect, useState } from "react";
import { profile, stats } from "../../mock";
import { ArrowUpRight, Github, Linkedin, MapPin, Mail, Sparkles } from "lucide-react";

const ROLES = [
  "intelligent systems",
  "RAG pipelines",
  "LLM agents",
  "data products",
];

function useTypewriter(words, typeSpeed = 80, deleteSpeed = 45, pause = 1400) {
  const [text, setText] = useState("");
  const [i, setI] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[i % words.length];
    let timer;

    if (!deleting && text === current) {
      timer = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && text === "") {
      setDeleting(false);
      setI((p) => (p + 1) % words.length);
    } else {
      timer = setTimeout(
        () => {
          setText((t) =>
            deleting ? current.substring(0, t.length - 1) : current.substring(0, t.length + 1)
          );
        },
        deleting ? deleteSpeed : typeSpeed
      );
    }
    return () => clearTimeout(timer);
  }, [text, deleting, i, words, typeSpeed, deleteSpeed, pause]);

  return text;
}

export default function Hero() {
  const typed = useTypewriter(ROLES);

  return (
    <section
      id="top"
      className="relative overflow-hidden pt-28 md:pt-36 pb-20 md:pb-28 mint-radial"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
      <div className="absolute inset-0 bg-noise opacity-[0.04] pointer-events-none" />
      <div className="aurora -top-40 -left-32" aria-hidden />
      <div className="aurora top-40 right-[-180px] opacity-25" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left: Copy */}
        <div className="lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 border border-zinc-800 rounded-full px-3 py-1.5 reveal"
          >
            <Sparkles size={12} className="text-[#5eead4]" />
            AI Engineer · Saudi Arabia
          </div>

          <h1
            className="display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] font-semibold mt-6 reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <span className="gradient-text">{profile.firstName}</span>{" "}
            <span className="text-zinc-500">{profile.lastName}.</span>
          </h1>

          <h2
            className="display text-3xl sm:text-4xl md:text-5xl text-zinc-400 mt-4 reveal min-h-[1.2em]"
            style={{ transitionDelay: "180ms" }}
          >
            I build{" "}
            <span className="text-white">{typed}</span>
            <span className="text-[#5eead4] cursor-blink ml-1">_</span>
          </h2>

          <p
            className="mt-7 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed reveal"
            style={{ transitionDelay: "240ms" }}
          >
            {profile.bio}
          </p>

          <div
            className="mt-10 flex flex-wrap items-center gap-3 reveal"
            style={{ transitionDelay: "300ms" }}
          >
            <a
              href="#projects"
              className="btn-primary-mint rounded-md px-5 py-3 text-sm font-medium mono inline-flex items-center gap-2"
            >
              View my work <ArrowUpRight size={16} />
            </a>
            <a
              href="#contact"
              className="btn-ghost rounded-md px-5 py-3 text-sm mono inline-flex items-center gap-2"
            >
              <Mail size={14} /> Contact me
            </a>
            <a
              href={profile.socials.github}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost rounded-md px-4 py-3 text-sm mono inline-flex items-center gap-2"
            >
              <Github size={16} /> GitHub
            </a>
            <a
              href={profile.socials.linkedin}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost rounded-md px-4 py-3 text-sm mono inline-flex items-center gap-2"
            >
              <Linkedin size={16} /> LinkedIn
            </a>
          </div>

          <div
            className="mt-6 flex items-center gap-2 text-xs mono text-zinc-500 reveal"
            style={{ transitionDelay: "360ms" }}
          >
            <MapPin size={14} className="text-[#5eead4]" /> {profile.location}
          </div>
        </div>

        {/* Right: Visual / Code-style card */}
        <div className="lg:col-span-5 reveal" style={{ transitionDelay: "240ms" }}>
          <CodeCard />
        </div>
      </div>

      {/* Stats */}
      <div className="relative max-w-6xl mx-auto px-6 md:px-8 mt-16">
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 rounded-xl overflow-hidden reveal"
          style={{ transitionDelay: "420ms" }}
        >
          {stats.map((s) => (
            <div key={s.label} className="bg-[#09090b] p-6 transition-colors hover:bg-[#0d0d10]">
              <div className="display text-3xl md:text-4xl font-semibold text-white">
                {s.value}
              </div>
              <div className="mono text-xs text-zinc-500 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CodeCard() {
  return (
    <div className="relative">
      {/* Decorative orbiting ring */}
      <div className="absolute -inset-6 rounded-3xl border border-[#5eead4]/10 slow-spin pointer-events-none" />
      <div className="absolute -top-3 -right-3 mono text-[10px] uppercase tracking-widest text-[#5eead4] border border-[#5eead4]/30 bg-[#5eead4]/5 px-2 py-1 rounded-full backdrop-blur float-soft">
        // engineer.profile
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-[#0b0b0d]/90 backdrop-blur shadow-[0_30px_80px_-30px_rgba(94,234,212,0.25)] overflow-hidden tilt">
        {/* window header */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-zinc-900 bg-[#09090b]">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ef4444]/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#f59e0b]/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#22c55e]/70" />
          <span className="ml-3 mono text-[11px] text-zinc-500">azzam.py</span>
        </div>

        {/* code body */}
        <pre className="p-5 mono text-[12.5px] leading-relaxed text-zinc-300 overflow-x-auto">
{`from ai import Engineer

azzam = Engineer(
  name="Azzam AlHarbi",
  role="`}<span className="text-[#5eead4]">AI Engineer</span>{`",
  focus=[`}<span className="text-[#a78bfa]">"RAG"</span>{`, `}<span className="text-[#a78bfa]">"LLM"</span>{`, `}<span className="text-[#a78bfa]">"NLP"</span>{`],
  stack=[`}<span className="text-[#a78bfa]">"Python"</span>{`, `}<span className="text-[#a78bfa]">"FastAPI"</span>{`,
         `}<span className="text-[#a78bfa]">"Django"</span>{`, `}<span className="text-[#a78bfa]">"PowerBI"</span>{`],
  status=`}<span className="text-[#5eead4]">"shipping intelligent systems"</span>{`,
)

azzam.build_with_you()  `}<span className="text-zinc-600"># let&apos;s talk</span>
        </pre>
      </div>
    </div>
  );
}
